import React, { useState } from 'react'
import { get } from 'lodash'
import { currency } from 'lib/utils/string'
import { useMutation } from '@apollo/client'
import { useSnackbar } from 'notistack'
import { useForm, FormProvider } from 'react-hook-form'
import { validationSchema } from '../utils'
import { yupResolver } from '@hookform/resolvers'
import {
  useStripe,
  useElements,
  CardNumberElement,
} from '@stripe/react-stripe-js'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import Avatar from '@material-ui/core/Avatar'
import Button from '@material-ui/core/Button'
import BookingTypeCard from 'components/booking-type-card'
import CircularProgress from '@material-ui/core/CircularProgress'
import { useAuth } from 'lib/auth'
import { withStripeElements, calculateStripeFee } from 'lib/stripe'
import StepDescription from '../step-description'
import { CREATE_LESSON_FOR_STUDENT_QUERY } from './query'
import PaymentInformation from './payment-information'
import { useStyles } from './styles'
import { gtmEvent } from 'lib/gtm'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import ArrowBackOutlinedIcon from '@material-ui/icons/ArrowBackOutlined'

const LessonReviewLayout = ({
  lessonType,
  locationId,
  startDateTime,
  student,
  previousUrl,
  isComplete,
  setIsComplete,
  setLastDigits,
  setCardBrand,
}) => {
  const {
    title,
    category,
    priceInCents,
    durationInMinutes,
    coach,
    facilities,
    acceptsOnlinePayments,
  } = lessonType
  const facility = facilities.find(({ id }) => id === locationId)

  const studentName = `${student.firstName} ${student.lastName}`
  const [isStripeFlatFeeDeducted] = useFeatureFlags([
    flags.FLAT_FEAT_STRIPE_FEE_DEDUCTED,
  ])
  const priceInDollars = priceInCents ? priceInCents / 100 : null
  const processingFeeInDollars = isStripeFlatFeeDeducted
    ? 0
    : calculateStripeFee(priceInCents || 0) / 100

  const { isCoach } = useAuth()
  const [paymentRequired, setPaymentRequired] = useState(
    !isCoach && acceptsOnlinePayments,
  )

  const { enqueueSnackbar } = useSnackbar()
  const [createLesson, { loading }] = useMutation(
    CREATE_LESSON_FOR_STUDENT_QUERY,
  )

  const form = useForm({
    resolver: yupResolver(validationSchema(paymentRequired)),
  })

  const stripe = useStripe()
  const elements = useElements()
  const classes = useStyles()

  const defaultRedirectUrl = `${window.location.origin}${
    isCoach ? '/pga-coach/bookings/schedule' : '/club'
  }`
  const onSubmit = (redirectUrl = defaultRedirectUrl) => async formValues => {
    const { error: stripeError, token: stripeToken } = await (paymentRequired
      ? stripe.createToken(elements.getElement(CardNumberElement), {
        name: formValues.payment.name,
        address_country: 'US',
      })
      : Promise.resolve({ error: null, token: null }))

    if (stripeError) {
      enqueueSnackbar(stripeError.message, {
        variant: 'error',
      })
      return Promise.resolve()
    }

    return createLesson({
      variables: {
        input: {
          studentId: student.id,
          lessonTypeId: lessonType.id,
          startDateTime,
          locationId: facility.id,
          stripeToken: stripeToken?.id,
        },
      },
    })
      .then(({ data }) => {
        const { success, message } = data.createLessonForStudent

        if (success) {
          enqueueSnackbar('Your session has been booked.', {
            variant: 'success',
          })
          gtmEvent({
            event: 'formSubmit',
            formCategory: 'book-session',
            formAction: 'clicked-book-session-only-button',
          })

          setIsComplete(!isComplete)
          setLastDigits(stripeToken?.card.last4)
          setCardBrand(stripeToken?.card.brand)
        } else {
          enqueueSnackbar(message, {
            variant: 'error',
          })
        }
      })
      .catch(error => {
        window.rg4js &&
          window.rg4js('send', {
            error: error,
            tags: ['bookings', 'form-submit'],
          })
        enqueueSnackbar('An error has occurred. Please try again later.', {
          variant: 'error',
        })
      })
  }

  if (loading) {
    return (
      <Container className={classes.root}>
        <div className={classes.loadingStyles}>
          <CircularProgress color="primary" />
        </div>
      </Container>
    )
  }

  return (
    <Container className={classes.root}>
      <Grid container>
        <Grid item xs={12}>
          <Box display="flex" flexGrow={1}>
            <Button href={previousUrl} startIcon={<ArrowBackOutlinedIcon />}>
              BACK
            </Button>
          </Box>
        </Grid>
        <Grid item md={7} xs={12}>
          <StepDescription
            stepText="Step 3 of 3"
            title={acceptsOnlinePayments ? 'Review and pay' : 'Review'}
            description="Please review the information below."
          />

          <div className={classes.rowWrapped}>
            <Avatar src={get(coach, 'coachProfile.profilePhoto')}>
              {coach.name.substring(0, 1)}
            </Avatar>
            <div className={classes.infoWrapped}>
              <Typography
                variant="caption"
                className={`${classes.fontBolded} ${classes.captionText}`}
              >
                {coach.name}
              </Typography>
              <Typography
                variant="caption"
                className={`${classes.captionText} ${classes.lighterText}`}
              >
                {get(coach, 'coachProfile.title', '')}
              </Typography>
            </div>
          </div>
          <BookingTypeCard
            category={category}
            facility={facility}
            title={title}
            startDateTime={startDateTime}
            durationInMinutes={durationInMinutes}
            isComplete={isComplete}
          />
          <div className={classes.rowWrapped}>
            <Avatar alt={studentName}>
              {`${student.firstName.substring(
                0,
                1,
              )}${student.lastName.substring(0, 1)}`}
            </Avatar>
            <div className={classes.infoWrapped}>
              <Typography
                variant="caption"
                className={`${classes.fontBolded} ${classes.captionText}`}
              >
                {studentName}
              </Typography>
              <Typography
                variant="caption"
                className={`${classes.captionText} ${classes.lighterText}`}
              >
                Student
              </Typography>
            </div>
          </div>
          <Divider className={classes.dividerStyles} />
          {priceInDollars !== null && (
            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body1" className={classes.lighterText}>
                  Subtotal
                </Typography>
                <Typography variant="body1" className={classes.lighterText}>
                  {currency(priceInDollars)}
                </Typography>
              </Box>
              {paymentRequired ? (
                <>
                  {!isStripeFlatFeeDeducted && (
                    <div className={classes.infoRow}>
                      <Typography
                        variant="caption"
                        className={classes.captionText}
                      >
                        Processing Fee
                      </Typography>
                      <Typography
                        variant="caption"
                        className={classes.captionText}
                      >
                        {currency(processingFeeInDollars)}
                      </Typography>
                    </div>
                  )}
                  <div className={classes.infoRow}>
                    <Typography
                      variant="body1"
                      className={`${classes.fontBolded}`}
                    >
                      Due Today
                    </Typography>
                    <Typography
                      variant="body1"
                      className={`${classes.fontBolded}`}
                    >
                      {currency(priceInDollars + processingFeeInDollars)}
                    </Typography>
                  </div>
                </>
              ) : (
                <>
                  <div className={classes.infoRow}>
                    <Typography
                      variant="body1"
                      className={`${classes.fontBolded}`}
                    >
                      Due Today
                    </Typography>
                    <Typography
                      variant="body1"
                      className={`${classes.fontBolded}`}
                    >
                      {currency(0)}
                    </Typography>
                  </div>
                  <div className={classes.infoRow}>
                    <Typography
                      variant="caption"
                      className={classes.captionText}
                    >
                      Pay at location
                    </Typography>
                  </div>
                </>
              )}
              <Divider className={classes.dividerStyles} />
            </Box>
          )}
          <FormProvider {...form}>
            <form>
              <Box mb={4}>
                <PaymentInformation
                  lessonType={lessonType}
                  paymentRequired={paymentRequired}
                  setPaymentRequired={setPaymentRequired}
                />
              </Box>
              <Grid item md={5}>
                <Box mb={4} textAlign="left">
                  <Box mb={4} textAlign="left">
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      onClick={form.handleSubmit(onSubmit())}
                    >
                      BOOK SESSION
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </form>
          </FormProvider>
        </Grid>
      </Grid>
    </Container>
  )
}

export default withStripeElements(LessonReviewLayout)
